import React, { useContext, useState, useEffect, useMemo, useRef, useCallback } from "react";
import { LAButton, LAText, LAView } from "../../components";
import HTMLContent from "../../components/HTMLContent";
import ExplanationComponent from "../../components/ExplanationComponent";
import AnswerMessage from "../../components/AnswerMessage";
import RelatedStudyArticles from "../../components/common/RelatedStudyArticles";
import { useLocation, useNavigate } from "react-router-dom";
import { STRINGS } from "../../constant";

// assets

// import { GET_QUESTION_FROM_ID } from "../../../content/index";
import { AuthContext } from "../../context/AuthProvider";
// lodash
import _ from "lodash";
//
import { GET_COMMUNITY_SINGLE_QUESTION_DETAILS } from "../../api/community";
import { GET_APP_DOMAIN_LIST, STRING } from "../../../app_constant";
import { BundleContext } from "../../context/BundleProvider";
import { ThemeContext } from "../../context/ThemeProvider";
import { useAnalytics } from "../../context/AnalyticsProvider";

import CommonHeader from "../../components/common/CommonHeader";
import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons";

// Components
import {
  QuestionsProgress,
  QuestionActions,
  QuestionResult,
  QuestionsFooter,
  CommunityInsightModal,
  EmptyQuestionsState
} from "../../components/questions";
import QuestionOptions from "../../components/common/QuestionOptions";
import NavBar from "../../components/NavBar";
import { GET_QUESTION_FROM_ID } from "../../api/test";
import UpgradeBanner from '../../components/common/UpgradeBanner';

const Questions = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { trackStudySessionStart, trackStudySessionComplete } = useAnalytics();
  const set_id = state?.set_id ?? "";
  const lastIndex = state?.lastIndex || 0;
  const questionSet = state?.questions || [];
  const title = state?.title ?? STRINGS.study_questions_title;
  const testing_mode = state?.testing_mode ?? false;
  const isShowAnswer = true;
  const [quickSetState, setQuickSetState] = useState({
    selectedOpion: null,
  });
  
  // Session tracking
  const sessionStartTime = React.useRef(Date.now());
  const sessionStats = React.useRef({
    questions_attempted: 0,
    correct_count: 0
  });
  
  const {
    profile,
    questionsList,
    addQuestionScore,
    addQuestionProgress,
    isVisibleCommunity,
    addQuestionToBookmark,
    removeQuestionFromBookmark,
    isSubscribe,
  } = useContext(AuthContext);
  const { theme, currentTheme } = useContext(ThemeContext);
  const { selectedCertificate } = useContext(BundleContext);
  const OPTIONS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'];
  const [isLoadingCommunityData, setLoadingCommunityData] = useState(false);
  const [questions, setQuestions] = useState(questionSet);

  const [currentIndex, setCurrentIndex] = useState(lastIndex);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [savedAnswers, setSavedAnswers] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [isSubmitAnswer, setSubmitAnswer] = useState(false);
  const [communityQuestion, setCommunityQuestion] = useState(null);
  const [showCommunityModal, setShowCommunityModal] = useState(false);
  const isBookmark = checkBookmark();
  const lastSavedQuestionIdRef = useRef(null);
  
  // Memoize currentItem to prevent unnecessary re-renders
  const currentItem = useMemo(() => {
    if (questions.length > currentIndex) {
      return GET_QUESTION_FROM_ID(questionsList, questions[currentIndex]?.id);
    }
    return null;
  }, [questions, currentIndex, questionsList]);
  
  const topic = getTopicId();

  const communitySettings = getCommunityQuestionPercentage();

  const styles = {
    questionCard: {
      background: theme.dashboard.card.background,
      border: `1px solid ${theme.dashboard.card.border}`,
      borderRadius: '1rem',
      boxShadow: theme.dashboard.card.shadow,
      marginBottom: '2rem',
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
    questionContent: {
      padding: '2rem',
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
    },
    questionText: {
      marginBottom: '2rem',
      color: theme.text.primary,
      fontSize: '1.125rem',
      lineHeight: '1.7',
    },
    optionsGrid: {
      display: 'grid',
      gap: '1rem',
      gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
      alignItems: 'stretch',
    },
  }

  useEffect(() => {
    setTimeout(() => {
      if (questions.length > lastIndex) {
        setCurrentIndex(lastIndex);
      }
    }, 300);
  }, []);

  const setSelectedAnswer = (questionId, selectedOption) => {
    setQuickSetState((prev) => ({
      ...prev,
      selectedOpion: selectedOption,
    }));
  };

  useEffect(() => {
    const question = questions[currentIndex];
    if (!question) return;
    
    // Prevent duplicate progress saves
    if (questions?.length > currentIndex && set_id && question?.id !== lastSavedQuestionIdRef.current) {
      addQuestionProgress(set_id, question?.id);
      lastSavedQuestionIdRef.current = question?.id;
    }

    const index = savedAnswers.findIndex((e) => e.question_id == question?.id);
    if (index >= 0) {
      setSelectedIndex({
        selected: savedAnswers[index].selected,
        is_from_older: true,
      });
    } else if (testing_mode && currentItem && currentItem.choices && currentItem.choices.length > 0) {
      // Auto-select random answer in testing mode if no saved answer exists
      const numChoices = currentItem.choices.length;
      
      // Check if it's a multi-answer question
      if (Array.isArray(currentItem.question?.correct_answer)) {
        // Multi-answer: pick 1-3 random options
        const numSelections = Math.min(Math.floor(Math.random() * 3) + 1, numChoices);
        const shuffled = _.shuffle([...Array(numChoices).keys()]);
        const randomIndices = shuffled.slice(0, numSelections).sort((a, b) => a - b);
        const randomSelection = randomIndices.map(idx => OPTIONS[idx]);
        
        // Save the answer
        setSavedAnswers(prev => {
          const exists = prev.findIndex((e) => e.question_id == question?.id);
          if (exists >= 0) {
            return prev;
          }
          return [...prev, {
            question_id: question.id,
            selected: randomSelection
          }];
        });
        
        // Set submit answer to true for multi-answer questions
        setSubmitAnswer(true);
        
        setSelectedIndex({ selected: randomSelection, is_from_older: false });
      } else {
        // Single-answer: pick one random option
        const randomIndex = Math.floor(Math.random() * numChoices);
        const randomSelection = OPTIONS[randomIndex];
        
        // Save the answer
        setSavedAnswers(prev => {
          const exists = prev.findIndex((e) => e.question_id == question?.id);
          if (exists >= 0) {
            return prev;
          }
          return [...prev, {
            question_id: question.id,
            selected: randomSelection
          }];
        });
        
        setSelectedIndex({ selected: randomSelection, is_from_older: false });
      }
    }

    // Skip community question fetching when testing_mode is true
    if (isVisibleCommunity && question && !testing_mode) {
      setCommunityQuestion(null);
      getCommunityQuestion(question?.id);
    }
  }, [currentIndex, testing_mode, currentItem?.question?.id, questions, set_id, savedAnswers, isVisibleCommunity, addQuestionProgress]);

  useEffect(() => {
    if (selectedIndex) {
      if (selectedIndex.is_from_older == false) {
        if (!Array.isArray(currentItem?.question?.correct_answer)) {
          onUpdateScore();
        }
      }
    }
  }, [selectedIndex]);

  // Session tracking useEffect
  useEffect(() => {
    // Track session start with new method
    if (title) {
      trackStudySessionStart(title, {
        mode: 'practice',
        question_count: questions?.length || 0,
        study_type: 'questions'
      });
    }

    // Clean up function to track session completion
    return () => {
      if (sessionStats.current.questions_attempted > 0) {
        const durationSeconds = Math.floor((Date.now() - sessionStartTime.current) / 1000);
        trackStudySessionComplete(title || 'Study Session', durationSeconds, sessionStats.current.questions_attempted, {
          correct_answers: sessionStats.current.correct_answers,
          accuracy_percentage: Math.round((sessionStats.current.correct_count / sessionStats.current.questions_attempted) * 100),
          study_type: 'questions'
        });
      }
    };
  }, [title, questions?.length, trackStudySessionStart, trackStudySessionComplete]);

  async function getCommunityQuestion(id) {
    try {
      setLoadingCommunityData(true);
      const result = await GET_COMMUNITY_SINGLE_QUESTION_DETAILS(id);
      setLoadingCommunityData(false);
      if (result.data) {
        setCommunityQuestion(result.data);
      } else {
        setCommunityQuestion(null);
      }
    } catch (error) {
      setLoading(false);
      setCommunityQuestion(null);
    }
  }

  function onUpdateScore() {
    let isCorrect = false;
    
    if (Array.isArray(currentItem?.question.correct_answer)) {
      if (selectedIndex && selectedIndex?.selected?.length > 0) {
        const answers = currentItem?.question.correct_answer;
        const selected = selectedIndex?.selected;

        isCorrect = true;
        answers.forEach((element) => {
          if (!selected.includes(element)) {
            isCorrect = false;
          }
        });

        // Skip tracking when in testing mode
        if (!testing_mode) {
          addQuestionScore(
            currentItem?.question.id,
            currentItem?.question.topic_id,
            isCorrect
          );
        }
      }
    } else {
      if (selectedIndex && selectedIndex.selected != "X") {
        isCorrect = currentItem?.question.correct_answer == selectedIndex.selected;
        
        // Skip tracking when in testing mode
        if (!testing_mode) {
          //UPDATE USER SCORE
          addQuestionScore(
            currentItem?.question.id,
            currentItem?.question.topic_id,
            isCorrect
          );
        }
      }
    }
    
    // Update session stats for analytics (no tracking individual answers)
    if (selectedIndex && selectedIndex.selected != "X") {
      sessionStats.current.questions_attempted++;
      if (isCorrect) {
        sessionStats.current.correct_count++;
      }
    }
  }

  function getCurrentQuestion() {
    if (questions.length > currentIndex) {
      return GET_QUESTION_FROM_ID(questionsList, questions[currentIndex]?.id);
    }

    return null;
  }

  function getTopicId() {
    if (questions.length > currentIndex) {
      const APP_DOMAIN_LIST = GET_APP_DOMAIN_LIST(selectedCertificate);
      const array = APP_DOMAIN_LIST.find(
        (item) => item?.id == questions[currentIndex]?.topic_id
      );
      return array?.domain_name ?? "";
    }

    return "";
  }

  function onPrevious() {
    setSelectedIndex(null);
    setSubmitAnswer(false);
    // scrollRef.current.scrollTo({ x: 0, y: 0, animated: false })

    setCurrentIndex((state) => {
      return state == 0 ? 0 : state - 1;
    });
  }

  function onNext() {
    setSelectedIndex(null);
    setSubmitAnswer(false);
    // scrollRef.current.scrollTo({ x: 0, y: 0, animated: false })

    setCurrentIndex((state) => {
      return state == questions.length - 1 ? 0 : state + 1;
    });
  }

  function onBookmark() {
    if (questions.length > currentIndex) {
      const question = questions[currentIndex];
      if (isBookmark) {
        
        removeQuestionFromBookmark(question?.id);
      } else {
        
        addQuestionToBookmark(question?.id);
      }
    }
  }

  function checkBookmark() {
    if (questions.length > currentIndex) {
      const question = questions[currentIndex];
      return profile?.question_bookmarks?.includes(question?.id) ?? false;
    }

    return false;
  }

  function getCommunityQuestionPercentage() {
    if (communityQuestion && true) {
      const percentage =
        (communityQuestion?.times_answered_correct /
          communityQuestion?.times_answered) *
        100;
      if (isNaN(percentage) || !isFinite(percentage) || percentage < 50) {
        return {
          color: theme?.primary?.red?.[500],
          percentage: 0,
          message: STRINGS.community_popup_desc_less_50,
          level: 'hard'
        };
      } else if (percentage < 80) {
        return {
          color: theme?.primary?.yellow?.[500],
          percentage: 0,
          message: STRINGS.community_popup_desc_less_80,
          level: 'medium'
        };
      } else {
        return {
          color: theme?.primary?.green?.[500],
          percentage: 0,
          message: STRINGS.community_popup_desc_less_100,
          level: 'easy'
        };
      }
    }

    return {
      color: theme?.primary?.red?.[500],
      percentage: 0,
      message: STRINGS.community_popup_desc_less_50,
      level: 'hard'
    };
  }
  

  return (
    <div className="min-h-screen transition-colors duration-300" 
         style={{ background: theme?.background?.main }}>
      <div>
        {/* Header */}
        
        <CommonHeader
          title={title}
          subtitle="Test your knowledge and track your progress"
          icon={faQuestionCircle}
          iconType="fontawesome"
          onBack={() => navigate(-1)}
          theme={theme}
          showBackButton={true}
        />

        {/* Progress Bar */}
        <QuestionsProgress
          currentIndex={currentIndex}
          totalQuestions={questions.length}
          isBookmarked={isBookmark}
          communityStats={getCommunityQuestionPercentage()}
          onBookmark={onBookmark}
          onShowCommunity={() => setShowCommunityModal(true)}
          theme={theme}
        />

        {/* Upgrade Banner for Free Users on Last Question */}
        {!isSubscribe && currentIndex === questions.length - 1 && questions.length > 0 && (
          <UpgradeBanner
            type="question"
            length={questionsList?.length}
            theme={theme}
            onUpgrade={() => navigate('/chooseplan')}
          />
        )}
        
        {currentItem && questions?.length > 0 ? (
          <div style={styles.questionCard}>
            <div style={styles.questionContent}>
            <div style={styles.questionText}>
            <HTMLContent
              index={currentItem?.question?.id}
              htmlContent={currentItem?.question?.question}
              style={{ 
            fontSize: '1.125rem',
            lineHeight: '1.75',
            color: theme?.text?.primary,
          }}
            />
            </div>

            <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
              {currentItem?.choices?.map((options, index) => {
                const isSelected = selectedIndex
                  ? selectedIndex.selected == OPTIONS[index]
                  : false;

                return (
                  <QuestionOptions
                    key={index}
                    index={index}
                    item={options}
                    isShowAnswer={isShowAnswer}
                    correct={currentItem?.question?.correct_answer}
                    selected={selectedIndex ? selectedIndex.selected : "X"}
                    isSelected={isSelected}
                    isSubmit={isSubmitAnswer || selectedIndex?.is_from_older}
                    onPress={() => {
                      if (
                        Array.isArray(currentItem?.question?.correct_answer)
                      ) {
                        const previousSelected =
                          selectedIndex?.selected ?? [];
                        if (previousSelected.includes(OPTIONS[index])) {
                          const filter = previousSelected.filter(
                            (e) => e != OPTIONS[index]
                          );
                          setSelectedIndex({
                            selected: filter,
                            is_from_older: false,
                          });
                        } else {
                          previousSelected.push(OPTIONS[index]);
                          setSelectedIndex({
                            selected: previousSelected,
                            is_from_older: false,
                          });
                        }
                      } else {
                        const selected = OPTIONS[index];
                        if (!selectedIndex) {
                          setSelectedIndex({
                            selected: selected,
                            is_from_older: false,
                          });

                          let answer = {
                            question_id: currentItem?.question?.id,
                            selected: selected,
                          };

                          const items = [...savedAnswers];
                          items.push(answer);
                          setSavedAnswers(items);
                        }
                      }
                    }}
                  />
                );
              })}
            </div>
            {isSubmitAnswer == false &&
              Array.isArray(currentItem?.question?.correct_answer) &&
              selectedIndex &&
              selectedIndex?.selected?.length > 0 &&
              !selectedIndex?.is_from_older && (
                <div className=" mt-10 w-1/2 text-center flex items-center justify-center mx-auto">
                  <LAButton
                    title="Submit Answer"
                    onPress={() => {
                      let answer = {
                        question_id: currentItem?.question?.id,
                        selected: selectedIndex.selected,
                      };

                      const items = [...savedAnswers];
                      items.push(answer);
                      setSavedAnswers(items);

                      setSubmitAnswer(true);

                      onUpdateScore();
                    }}
                  />
                </div>
              )}
            {isShowAnswer &&
              selectedIndex &&
              selectedIndex.selected != "X" &&
              (!Array.isArray(currentItem?.question?.correct_answer) ||
                isSubmitAnswer ||
                (selectedIndex && selectedIndex?.is_from_older)) && (
                <div className="pt-6">
                  <AnswerMessage
                    correct_answer={currentItem?.question?.correct_answer}
                    selected_answer={selectedIndex.selected}
                  />
                  <ExplanationComponent
                    explanation={currentItem.question?.explanation ?? ""}
                    topic={topic}
                    sub_domain={
                      currentItem?.question?.sub_domain
                        ? currentItem?.question?.sub_domain
                        : ""
                    }
                  />
                  <RelatedStudyArticles
                    relatedArticles={currentItem?.question?.related_articles}
                    currentQuestion={currentItem}
                  />
                </div>
              )}
          </div>
          </div>
        ) : (
          <div className="text-center mt-4">No questions available</div>
        )}
      </div>

      {questions?.length > 0 && (
        
          <NavBar
        type="test"
        onNext={onNext}
        onPrevious={onPrevious}
        currentIndex={currentIndex}
        dataSet={questions.length ?? 0}
        navTitle="Question"
        showEnd={false}
        onEndTest={() => {}}
      />
        
        
      )}

      {/* Community Modal */}
      <CommunityInsightModal
        isOpen={showCommunityModal}
        onClose={() => setShowCommunityModal(false)}
        communityStats={getCommunityQuestionPercentage()}
        theme={theme}
      />
    </div>
  );
};

export default Questions;
